import React, { useMemo } from "react";
import { Card, Typography, Table, Divider, Row, Col, Tooltip } from "antd";
import {
  Invoice,
  InvoiceLine,
  PaymentTerm,
  paymentTermLabels,
} from "../../types/invoice";
import { Booking, Product } from "../../types/booking";
import { InfoCircleOutlined } from "@ant-design/icons";
import { AIR_FREIGHT } from "../../lib/pricingDefaults";
import { useCostOverviewDisplayOrder } from "../../hooks/useCostOverviewDisplayOrder";

const { Title, Text } = Typography;

interface InvoicePreviewProps {
  invoice: Partial<Invoice> & { lines?: InvoiceLine[] };
  booking: Booking | null;
}

// Utility function to format currency
const formatCurrency = (amount: number, currency: string = "EUR") => {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

// Helper to get flattened product list from booking
const getBookingProducts = (booking: Booking | null): Product[] => {
  if (!booking || !booking.product_info) return [];

  // Handle different structures of product_info from booking_product table
  if (Array.isArray(booking.product_info)) {
    // Already an array of products
    return booking.product_info as unknown as Product[];
  }

  // Single product
  return [booking.product_info as unknown as Product];
};

// Get incoterm from booking
const getIncoterm = (booking: Booking | null) => {
  if (!booking || !booking.booking_incoterm_info) return null;

  return Array.isArray(booking.booking_incoterm_info)
    ? booking.booking_incoterm_info[0]
    : booking.booking_incoterm_info;
};

// Get container reference from booking
const getContainerReference = (booking: Booking | null) => {
  if (!booking || !booking.container_info) return null;

  const containerInfo = Array.isArray(booking.container_info)
    ? booking.container_info[0]
    : booking.container_info;

  return containerInfo?.reference || null;
};

// Get payment term display text
const getPaymentTermText = (invoice: Partial<Invoice>) => {
  if (!invoice.payment_term) return paymentTermLabels[PaymentTerm.DAYS_30];

  if (
    invoice.payment_term === PaymentTerm.CUSTOM &&
    invoice.custom_payment_terms
  ) {
    return invoice.custom_payment_terms;
  }

  return paymentTermLabels[invoice.payment_term] || paymentTermLabels[PaymentTerm.DAYS_30];
};

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoice,
  booking,
}) => {
  const sortCostOverview = useCostOverviewDisplayOrder();

  const sortedLines = useMemo(() => {
    const lines = (invoice.lines || []).filter((line) => !!line);
    return [...lines].sort((a, b) =>
      sortCostOverview(
        { cost_type: a.cost_type, description: a.description },
        { cost_type: b.cost_type, description: b.description }
      )
    );
  }, [invoice.lines, sortCostOverview]);

  // Calculate subtotal
  const subtotal = sortedLines.reduce((sum, line) => {
    if (!line) return sum;
    return sum + (line.quantity || 0) * (line.unit_price || 0);
  }, 0);

  // Calculate VAT
  const vat = sortedLines.reduce((sum, line) => {
    if (!line) return sum;
    const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
    return sum + (lineTotal * (line.vat_percentage || 0)) / 100;
  }, 0);

  // Calculate total
  const total = subtotal + vat;

  // Format date as DD-MM-YYYY
  const formatDate = (date: string) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${d.getFullYear()}`;
  };

  // Get company info
  const getCompanyInfo = () => {
    if (!booking || !booking.company_info) return null;

    const companyInfo = Array.isArray(booking.company_info)
      ? booking.company_info[0]
      : booking.company_info;

    return companyInfo;
  };

  // Format booking reference
  const getBookingReference = () => {
    if (!booking) return "";

    const reference = booking.reference || `EFF${booking.id}`;
    const transportType = booking.transport_type_info
      ? Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.abbreviation || ""
        : booking.transport_type_info.abbreviation || ""
      : "";

    // Get transport type ID to check if it's seafreight (transport_type 2)
    const transportTypeId = booking.transport_type_info
      ? Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.id
        : booking.transport_type_info.id
      : null;

    // Add booking type for seafreight (transport_type 2)
    let bookingTypeInfo = "";
    if (transportTypeId === 2 && booking.booking_type_info) {
      const bookingType = Array.isArray(booking.booking_type_info)
        ? booking.booking_type_info[0]?.name || ""
        : booking.booking_type_info.name || "";

      if (bookingType) {
        bookingTypeInfo = `-${bookingType}`;
      }
    }

    return `${reference}${transportType}${bookingTypeInfo}`;
  };

  // Get origin port info, prioritizing container origin if available
  const getOriginPort = () => {
    if (!booking) return "";

    // Check if booking has container with origin port
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;

      if (containerInfo?.origin_port) {
        const originPort = Array.isArray(containerInfo.origin_port)
          ? containerInfo.origin_port[0]
          : containerInfo.origin_port;

        if (originPort && originPort.name) {
          return originPort.name;
        }
      }
    }

    // Fallback to booking origin port
    const originPort =
      booking.origin_port_info &&
      (Array.isArray(booking.origin_port_info)
        ? booking.origin_port_info[0]
        : booking.origin_port_info);

    return originPort?.name || "Unknown";
  };

  // Get destination port info, prioritizing container destination if available
  const getDestinationPort = () => {
    if (!booking) return "";

    // Check if booking has container with destination port
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;

      if (containerInfo?.destination_port) {
        const destPort = Array.isArray(containerInfo.destination_port)
          ? containerInfo.destination_port[0]
          : containerInfo.destination_port;

        if (destPort && destPort.name) {
          return destPort.name;
        }
      }
    }

    // Fallback to booking destination port
    const destPort =
      booking.destination_port_info &&
      (Array.isArray(booking.destination_port_info)
        ? booking.destination_port_info[0]
        : booking.destination_port_info);

    return destPort?.name || "Unknown";
  };

  // Get shipping details (weight, volume, chargeable weight)
  const getShippingDetails = () => {
    if (!booking)
      return {
        totalWeight: 0,
        totalVolume: 0,
        volumetricWeight: 0,
        chargeableWeight: 0,
        isAirFreight: false,
      };

    // Check if it's air freight
    const isAirFreight =
      booking.transport_type_info &&
      (Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.id === 1
        : booking.transport_type_info.id === 1);

    // Get flattened products
    const products = getBookingProducts(booking);

    // Calculate total weight
    const totalWeight = products.reduce(
      (sum, product) => sum + (product.gross_weight || 0),
      0
    );

    // Calculate total volume
    const totalVolume = products.reduce(
      (sum, product) => sum + (product.cbm || 0),
      0
    );

    // Calculate volumetric weight for air freight
    const volumetricWeight = totalVolume * AIR_FREIGHT.VOLUMETRIC_FACTOR;

    // Calculate chargeable weight (higher of actual and volumetric)
    const chargeableWeight = Math.max(totalWeight, volumetricWeight);

    return {
      totalWeight,
      totalVolume,
      volumetricWeight,
      chargeableWeight,
      isAirFreight,
    };
  };

  // Get products
  const getProducts = () => {
    if (!booking) return [];
    return getBookingProducts(booking);
  };

  const company = getCompanyInfo();
  const originPort = getOriginPort();
  const destinationPort = getDestinationPort();
  const shippingDetails = getShippingDetails();
  const products = getProducts();
  const incoterm = getIncoterm(booking);
  const containerRef = getContainerReference(booking);
  const paymentTermText = getPaymentTermText(invoice);

  return (
    <Card style={{ height: "100%" }} size="small">
      <div className="invoice-preview" style={{ padding: "12px" }}>
        <Row>
          <Col span={12}>
            <img
              src="/logo_black.png"
              alt="E-Freight Forwarding"
              style={{ height: 40, marginBottom: 8 }}
              onError={(e) => {
                e.currentTarget.src = "/logo_black.png";
              }}
            />
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Title level={4}>Factuur</Title>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={12}>
            <Text style={{ fontSize: "12px" }}>
              <Text strong>{company?.name || ""}</Text>
              <br />
              {/* Address information */}
              {company?.street && company?.house_number && (
                <>
                  {company.street} {company.house_number}
                  <br />
                </>
              )}
              {company?.postal_code && company?.city && (
                <>
                  {company?.postal_code} {company?.city}
                </>
              )}
              {company?.country && (
                <>
                  <br />
                  {company?.country}
                </>
              )}

              {/* Contact information */}
              {company?.phone_number && (
                <>
                  <br />
                  <Text strong>Phone:</Text> {company.phone_number}
                </>
              )}

              {/* Tax and registration information */}
              {company &&
                typeof company === "object" &&
                "vat_number" in company &&
                typeof company.vat_number === "string" && (
                  <>
                    <br />
                    <Text strong>VAT:</Text> {company.vat_number}
                  </>
                )}
              {company &&
                typeof company === "object" &&
                "eori_number" in company &&
                typeof company.eori_number === "string" && (
                  <>
                    <br />
                    <Text strong>EORI:</Text> {company.eori_number}
                  </>
                )}
              {company &&
                typeof company === "object" &&
                "coc_number" in company &&
                company.coc_number && (
                  <>
                    <br />
                    <Text strong>CoC:</Text> {company.coc_number}
                  </>
                )}
              {company &&
                typeof company === "object" &&
                "article_23" in company &&
                company.article_23 === true && (
                  <>
                    <br />
                    <Text strong>Article 23:</Text>{" "}
                    <Text type="success">Applicable</Text>
                  </>
                )}
            </Text>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Text style={{ fontSize: "12px" }}>
              E-Freight Forwarding BV
              <br />
              Ir Ds Tuijnmanweg 1A, 4131 PN Vianen
              <br />
              The Netherlands
              <br />
              <Text strong>VAT:</Text> NL000000000B01
              <br />
              <div style={{ marginTop: 4 }}>
                <Text strong>Date:</Text>{" "}
                {formatDate(invoice.created_at || new Date().toISOString())}
              </div>
            </Text>
          </Col>
        </Row>

        <Divider style={{ margin: "8px 0" }} />

        <Row style={{ marginBottom: 8 }}>
          <Col span={24}>
            <Text style={{ fontSize: "12px" }}>
              <Text strong>Booking Reference:</Text> {getBookingReference()}
              <br />
              <Text strong>Route:</Text> {originPort} - {destinationPort}
              <br />
              {incoterm && (
                <>
                  <Text strong>Incoterm:</Text> {incoterm.name}
                  <br />
                </>
              )}
              {containerRef && (
                <>
                  <Text strong>Container:</Text> {containerRef}
                  <br />
                </>
              )}
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Weight:</Text>{" "}
                  {shippingDetails.totalWeight.toFixed(2)} kg
                  <br />
                  <Text strong>Volume:</Text>{" "}
                  {shippingDetails.totalVolume.toFixed(2)} m³
                </Col>
                {shippingDetails.isAirFreight && (
                  <Col span={12}>
                    <Text strong>Chargeable Weight:</Text>{" "}
                    <Text type="danger">
                      {shippingDetails.chargeableWeight.toFixed(2)} kg
                    </Text>
                    <Tooltip title="The higher of actual weight and volumetric weight (1 CBM = 167 kg)">
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, fontSize: "10px" }}
                      />
                    </Tooltip>
                  </Col>
                )}
              </Row>
            </Text>
          </Col>
        </Row>

        {products.length > 0 && (
          <>
            <Text style={{ fontSize: "12px" }}>
              <Text strong>Products ({products.length}):</Text>
            </Text>
            <Table
              dataSource={products}
              size="small"
              pagination={false}
              style={{ fontSize: "10px", marginBottom: 12 }}
              rowKey="id"
              columns={[
                {
                  title: "Name",
                  dataIndex: "name",
                  key: "name",
                  width: "40%",
                  render: (text) => (
                    <Text
                      style={{ fontSize: "10px" }}
                      ellipsis={{ tooltip: text }}
                    >
                      {text}
                    </Text>
                  ),
                },
                {
                  title: "Cartons",
                  dataIndex: "cartons",
                  key: "cartons",
                  align: "right",
                  render: (cartons) => (
                    <Text style={{ fontSize: "10px" }}>{cartons || 0}</Text>
                  ),
                },
                {
                  title: "Weight (kg)",
                  dataIndex: "gross_weight",
                  key: "gross_weight",
                  align: "right",
                  render: (weight) => (
                    <Text style={{ fontSize: "10px" }}>
                      {(weight || 0).toFixed(2)}
                    </Text>
                  ),
                },
                {
                  title: "Volume (m³)",
                  dataIndex: "cbm",
                  key: "cbm",
                  align: "right",
                  render: (cbm) => (
                    <Text style={{ fontSize: "10px" }}>
                      {(cbm || 0).toFixed(3)}
                    </Text>
                  ),
                },
              ]}
            />
          </>
        )}

        <Table
          dataSource={sortedLines}
          pagination={false}
          rowKey={(record, index) => index?.toString() || "0"}
          size="small"
          style={{ fontSize: "12px" }}
          columns={[
            {
              title: "Description",
              dataIndex: "description",
              key: "description",
              width: "40%",
            },
            {
              title: "Qty",
              dataIndex: "quantity",
              key: "quantity",
              align: "right" as const,
              render: (quantity) => quantity || 0,
            },
            {
              title: "Unit Price",
              dataIndex: "unit_price",
              key: "unit_price",
              align: "right" as const,
              render: (price) =>
                formatCurrency(price || 0, invoice.currency || "EUR"),
            },
            {
              title: "VAT %",
              dataIndex: "vat_percentage",
              key: "vat_percentage",
              align: "right" as const,
              render: (vat) => `${vat || 0}%`,
            },
            {
              title: "Amount",
              key: "amount",
              align: "right" as const,
              render: (_, record) =>
                formatCurrency(
                  (record.quantity || 0) * (record.unit_price || 0),
                  invoice.currency || "EUR"
                ),
            },
          ]}
          summary={() => (
            <Table.Summary>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4} align="right">
                  <Text strong>Subtotal:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>
                    {formatCurrency(subtotal, invoice.currency || "EUR")}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4} align="right">
                  <Text strong>VAT:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>
                    {formatCurrency(vat, invoice.currency || "EUR")}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4} align="right">
                  <Text strong>Total:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>
                    {formatCurrency(total, invoice.currency || "EUR")}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        <Divider style={{ margin: "8px 0" }} />

        <Row style={{ marginTop: 8 }}>
          <Col span={24}>
            <Text style={{ fontSize: "12px" }}>
              <Text strong>Payment Information</Text>
              <br />
              Bank: ABN AMRO | IBAN: NL81ABNA0100116779 (EUR) | BIC: ABNANL2A
              <br />
              <Text strong>Payment Terms: {paymentTermText}</Text>
            </Text>
          </Col>
        </Row>
      </div>
    </Card>
  );
};

export default InvoicePreview;
